import React from 'react';
import PartnerPageSEO from './PartnerPageSEO';

// Partner Benefits Component
function PartnerBenefits() {
    const benefits = [
        {
            title: "Carbon Offsetting",
            icon: "fa-leaf",
            description: "Offset your company's carbon footprint with verified carbon credits from sustainable computing initiatives."
        },
        {
            title: "ESG Reporting",
            icon: "fa-file-chart-line",
            description: "Detailed impact reports for your ESG objectives and stakeholder communications."
        },
        {
            title: "Brand Visibility",
            icon: "fa-bullhorn",
            description: "Showcase your commitment to sustainability with co-branded initiatives."
        },
        {
            title: "Tech Access",
            icon: "fa-microchip",
            description: "Priority access to our sustainable computing technologies and research."
        }
    ];

    return (
        <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">Partner <span className="text-green-400">Benefits</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                    <div key={index} className="bg-gradient-to-b from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700 text-center transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-green-500/30">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-500/30 to-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className={`fas ${benefit.icon} text-green-400 text-2xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold mb-4">{benefit.title}</h3>
                        <p className="text-gray-300">{benefit.description}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ESG Platform Subscription Component
function ESGPlatformSubscription({ onRequestInfo }) {
    const subscriptions = [
        {
            id: "basic",
            name: "Starter",
            price: "50",
            features: [
                "ESG data collection & centralization",
                "Carbon footprint calculation",
                "Basic ESG metrics dashboard",
                "Standard regulatory reporting",
                "Email support",
                "Up to 5 users"
            ]
        },
        {
            id: "professional",
            name: "Professional",
            price: "150",
            popular: true,
            features: [
                "All Starter features",
                "Advanced analytics & benchmarking",
                "Custom metrics & KPI tracking",
                "Automated data collection",
                "TCFD & SASB framework alignment",
                "Priority support",
                "Up to 15 users"
            ]
        },
        {
            id: "enterprise",
            name: "Enterprise",
            price: "500",
            features: [
                "All Professional features",
                "Custom dashboards & white labeling",
                "API integrations & data connectors",
                "Supply chain ESG assessment",
                "Advanced scenario analysis",
                "Dedicated account manager",
                "Unlimited users"
            ]
        }
    ];

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {subscriptions.map((sub) => (
                    <div key={sub.id} className={`bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${sub.popular ? 'ring-2 ring-green-400 shadow-lg shadow-green-500/20' : 'border border-gray-700'}`}>
                        {sub.popular && (
                            <div className="bg-gradient-to-r from-green-600 to-green-400 text-white text-center py-2 font-medium">
                                Most Popular
                            </div>
                        )}
                        <div className="p-8">
                            <h3 className="text-2xl font-bold mb-2">{sub.name}</h3>
                            <div className="flex items-baseline mb-6">
                                <span className="text-3xl font-bold">£{sub.price}</span>
                                <span className="text-gray-400 ml-2">/month</span>
                            </div>
                            <ul className="space-y-3 mb-8">
                                {sub.features.map((feature, index) => (
                                    <li key={index} className="flex items-center">
                                        <i className="fas fa-check text-green-400 mr-3"></i>
                                        <span className="text-gray-300">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            <button 
                                onClick={() => onRequestInfo(`esg-${sub.id}`)}
                                className={`w-full py-3 rounded-lg ${
                                    sub.popular 
                                        ? 'bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 text-white' 
                                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                                } transition-all`}
                            >
                                {sub.popular ? 'Get Started Now' : 'Request Information'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Added ESG platform benefits for SEO */}
            <div className="mt-12 bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-8 rounded-xl border border-gray-700">
                <h3 className="text-2xl font-bold mb-6 text-center">Why Choose Our ESG Platform?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-shield-check text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Regulatory Compliance</h4>
                            <p className="text-gray-300">Stay ahead of evolving ESG regulations with built-in compliance frameworks for TCFD, SASB, GRI, and more.</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-tachometer-alt text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Performance Improvement</h4>
                            <p className="text-gray-300">Track your ESG performance over time, identify trends, and implement data-driven sustainability initiatives.</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-file-alt text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Simplified Reporting</h4>
                            <p className="text-gray-300">Generate professional ESG reports with one click for investors, stakeholders, and regulatory submissions.</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="bg-green-500/20 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                            <i className="fas fa-users text-green-400 text-xl"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-lg mb-2">Stakeholder Engagement</h4>
                            <p className="text-gray-300">Impress investors and customers with transparent, data-backed sustainability reporting and communications.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Carbon Credits Component
function CarbonCreditsOffering({ onRequestInfo }) {
    const carbonPricing = {
        price: "65",
        minPurchase: 10,
        features: [
            "Third-party verified carbon credits",
            "Detailed impact certificate",
            "Project-specific traceability",
            "Corporate ESG reporting compatibility",
            "Annual carbon offset statement"
        ],
        packages: [
            { tons: 25, label: "Small Business", discount: "0%" },
            { tons: 100, label: "Medium Business", discount: "5%" },
            { tons: 500, label: "Enterprise", discount: "10%" },
            { tons: 1000, label: "Corporate", discount: "15%" }
        ]
    };

    return (
        <div>
            <div className="bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700 max-w-3xl mx-auto transform transition-all duration-300 hover:shadow-xl hover:shadow-green-500/10 hover:border-green-500/30">
                <div className="p-8">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold">Carbon Credits</h3>
                        <div className="bg-gradient-to-r from-green-500/30 to-green-400/30 px-6 py-3 rounded-full">
                            <span className="text-green-400 font-bold text-xl">£{carbonPricing.price}</span>
                            <span className="text-green-400">/ton</span>
                        </div>
                    </div>
                    
                    <p className="text-gray-300 mb-6 text-lg">
                        Our carbon credits are sourced from verified sustainable technology projects that reduce CO₂ emissions while supporting innovation.
                    </p>
                    
                    <div className="bg-gray-700/50 p-5 rounded-lg mb-6 border border-gray-600/50">
                        <p className="font-medium flex items-center">
                            <i className="fas fa-info-circle text-blue-400 mr-2"></i>
                            Minimum purchase: {carbonPricing.minPurchase} tons
                        </p>
                    </div>
                    
                    <h4 className="font-bold mb-4 text-lg flex items-center">
                        <i className="fas fa-check-circle text-green-400 mr-2"></i>
                        All carbon credits include:
                    </h4>
                    <ul className="space-y-3 mb-8 bg-gray-800/50 p-5 rounded-lg">
                        {carbonPricing.features.map((feature, index) => (
                            <li key={index} className="flex items-center">
                                <i className="fas fa-check text-green-400 mr-3"></i>
                                <span className="text-gray-300">{feature}</span>
                            </li>
                        ))}
                    </ul>
                    
                    <h4 className="font-bold mb-4 text-lg flex items-center">
                        <i className="fas fa-tags text-green-400 mr-2"></i>
                        Volume Pricing:
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        {carbonPricing.packages.map((pkg, index) => (
                            <div key={index} className="bg-gray-700/50 p-4 rounded-lg text-center border border-gray-600/30 hover:border-green-500/30 transition-all hover:bg-gray-700/80">
                                <p className="font-bold text-lg">{pkg.tons}+ tons</p>
                                <p className="text-sm text-gray-300">{pkg.label}</p>
                                <p className="text-green-400 font-bold">{pkg.discount} discount</p>
                            </div>
                        ))}
                    </div>
                    
                    <button 
                        onClick={() => onRequestInfo('carbon-credits')}
                        className="w-full py-3 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white transition-all"
                    >
                        Purchase Carbon Credits
                    </button>
                </div>
            </div>
        </div>
    );
}

// Sequoia Tons Component
function SequoiaTonsOffering({ onRequestInfo }) {
    const sequoiaTiers = [
        {
            id: "standard",
            name: "Standard Commitment",
            timespan: "25-year",
            price: "85",
            features: [
                "25-year carbon sequestration guarantee",
                "Basic impact certificate",
                "Annual project updates",
                "Company listing on conservation page",
                "Digital badge for your website"
            ]
        },
        {
            id: "enhanced",
            name: "Enhanced Commitment",
            timespan: "50-year",
            price: "125",
            popular: true,
            features: [
                "50-year carbon sequestration guarantee",
                "Enhanced impact certificate",
                "Semi-annual detailed project reports",
                "Featured partner story on our website",
                "Co-branded sustainability assets",
                "Conservation area naming rights (for bulk purchases)"
            ]
        },
        {
            id: "legacy",
            name: "Legacy Commitment",
            timespan: "100-year",
            price: "195",
            features: [
                "100-year carbon sequestration guarantee",
                "Premium impact certificate and plaque",
                "Quarterly detailed project reports",
                "On-site recognition at sequoia forest",
                "Executive tour of conservation site",
                "Custom impact storytelling and media assets",
                "Legacy conservation naming rights"
            ]
        }
    ];

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {sequoiaTiers.map((tier) => (
                    <div key={tier.id} className={`bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${tier.popular ? 'ring-2 ring-green-400 shadow-lg shadow-green-500/20' : 'border border-gray-700'}`}>
                        {tier.popular && (
                            <div className="bg-gradient-to-r from-green-600 to-green-400 text-white text-center py-2 font-medium">
                                Most Popular
                            </div>
                        )}
                        <div className="p-8">
                            <h3 className="text-2xl font-bold mb-4">{tier.name}</h3>
                            <div className="bg-green-900/30 text-center py-3 px-4 rounded-lg mb-6 border border-green-900/50">
                                <span className="text-green-400 font-bold text-lg">{tier.timespan} Commitment</span>
                            </div>
                            <div className="flex items-baseline mb-6">
                                <span className="text-3xl font-bold">£{tier.price}</span>
                                <span className="text-gray-400 ml-2">/ton</span>
                            </div>
                            <ul className="space-y-3 mb-8 bg-gray-800/50 p-4 rounded-lg">
                                {tier.features.map((feature, index) => (
                                    <li key={index} className="flex items-center">
                                        <i className="fas fa-check text-green-400 mr-3"></i>
                                        <span className="text-gray-300">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            <button 
                                onClick={() => onRequestInfo(`sequoia-${tier.id}`)}
                                className={`w-full py-3 rounded-lg ${
                                    tier.popular 
                                        ? 'bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 text-white' 
                                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                                } transition-all`}
                            >
                                Purchase {tier.name}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="mt-12 bg-gradient-to-b from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-xl p-8 border border-gray-700 shadow-lg">
                <h3 className="text-2xl font-bold mb-6 text-center">About Our Sequoia Conservation Commitments</h3>
                <p className="text-gray-300 mb-8 text-center max-w-3xl mx-auto">
                    Our Sequoia Tons program offers different commitment periods for carbon sequestration, allowing you to choose the timeframe that aligns with your organization's sustainability goals:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/5">
                        <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <i className="fas fa-seedling text-green-400 text-2xl"></i>
                        </div>
                        <p className="font-bold text-xl mb-3 text-center">25-Year Commitment</p>
                        <p className="text-sm text-gray-300">Our entry-level commitment ensures carbon remains sequestered for at least 25 years, with regular monitoring and verification.</p>
                    </div>
                    <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/5">
                        <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <i className="fas fa-tree text-green-400 text-2xl"></i>
                        </div>
                        <p className="font-bold text-xl mb-3 text-center">50-Year Commitment</p>
                        <p className="text-sm text-gray-300">Our enhanced commitment includes additional protections and management to ensure carbon remains sequestered for half a century.</p>
                    </div>
                    <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/5">
                        <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <i className="fas fa-mountain text-green-400 text-2xl"></i>
                        </div>
                        <p className="font-bold text-xl mb-3 text-center">100-Year Commitment</p>
                        <p className="text-sm text-gray-300">Our legacy commitment provides the highest level of protection, ensuring sequestered carbon remains in place for a century or more.</p>
                    </div>
                </div>
                
                <div className="bg-blue-900/20 border border-blue-700/30 p-5 rounded-xl">
                    <p className="text-gray-300 text-center">
                        <i className="fas fa-info-circle text-blue-400 mr-2"></i>
                        All commitment levels include legal protections, regular monitoring, and verification by independent third parties to ensure the long-term viability of carbon sequestration.
                    </p>
                </div>
            </div>
        </div>
    );
}

// Partnership Form Component
function PartnershipForm({ selectedTier }) {
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        company: '',
        position: '',
        phone: '',
        companySize: '',
        interests: selectedTier || 'esg-platform', // Default to ESG platform
        message: '',
    });
    const [submitStatus, setSubmitStatus] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (selectedTier) {
            setFormData(prev => ({
                ...prev,
                interests: selectedTier
            }));
        }
    }, [selectedTier]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            console.log('Submission data:', formData);
            setSubmitStatus('success');
            
            // Reset form after successful submission
            setFormData({
                name: '',
                email: '',
                company: '',
                position: '',
                phone: '',
                companySize: '',
                interests: 'esg-platform',
                message: ''
            });
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
            <h2 className="text-2xl font-bold mb-6">Schedule Your ESG Platform Demo</h2>
            {submitStatus === 'success' ? (
                <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-500/30 to-green-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-check text-green-400 text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
                    <p className="text-gray-300 mb-8 text-lg">
                        We've received your request for an ESG platform demo. One of our specialists will contact you within 24 hours to schedule your personalized walkthrough.
                    </p>
                    <button 
                        onClick={() => setSubmitStatus(null)}
                        className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white transition-all transform hover:scale-105"
                    >
                        Request Another Demo
                    </button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-user text-blue-400 mr-2"></i> 
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-envelope text-blue-400 mr-2"></i>
                                Business Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="you@company.com"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-building text-blue-400 mr-2"></i>
                                Company Name *
                            </label>
                            <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your company"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-id-badge text-blue-400 mr-2"></i>
                                Job Title *
                            </label>
                            <input
                                type="text"
                                name="position"
                                value={formData.position}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your position"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-phone text-blue-400 mr-2"></i>
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Your phone number"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2 flex items-center">
                                <i className="fas fa-users text-blue-400 mr-2"></i>
                                Company Size
                            </label>
                            <select
                                name="companySize"
                                value={formData.companySize}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                                <option value="">Select company size</option>
                                <option value="1-50">1-50 employees</option>
                                <option value="51-200">51-200 employees</option>
                                <option value="201-500">201-500 employees</option>
                                <option value="501-1000">501-1000 employees</option>
                                <option value="1001-5000">1001-5000 employees</option>
                                <option value="5000+">5000+ employees</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-2 flex items-center">
                            <i className="fas fa-star text-blue-400 mr-2"></i>
                            I'm interested in:
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div 
                                className={`p-3 rounded-lg cursor-pointer transition-all flex items-center ${
                                    formData.interests.includes('esg') || formData.interests === 'esg-platform'
                                        ? 'bg-green-500/20 border border-green-500 text-white'
                                        : 'bg-gray-700/70 border border-gray-600 hover:bg-gray-600 text-gray-300'
                                }`}
                                onClick={() => setFormData({...formData, interests: 'esg-platform'})}
                            >
                                <i className="fas fa-chart-line text-green-400 mr-2"></i>
                                ESG Platform
                            </div>
                            <div 
                                className={`p-3 rounded-lg cursor-pointer transition-all flex items-center ${
                                    formData.interests.includes('carbon') || formData.interests === 'carbon-credits'
                                        ? 'bg-green-500/20 border border-green-500 text-white'
                                        : 'bg-gray-700/70 border border-gray-600 hover:bg-gray-600 text-gray-300'
                                }`}
                                onClick={() => setFormData({...formData, interests: 'carbon-credits'})}
                            >
                                <i className="fas fa-leaf text-green-400 mr-2"></i>
                                Carbon Credits
                            </div>
                            <div 
                                className={`p-3 rounded-lg cursor-pointer transition-all flex items-center ${
                                    formData.interests.includes('sequoia')
                                        ? 'bg-green-500/20 border border-green-500 text-white'
                                        : 'bg-gray-700/70 border border-gray-600 hover:bg-gray-600 text-gray-300'
                                }`}
                                onClick={() => setFormData({...formData, interests: formData.interests.includes('sequoia') ? formData.interests : 'sequoia-standard'})}
                            >
                                <i className="fas fa-tree text-green-400 mr-2"></i>
                                Sequoia Tons
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-2 flex items-center">
                            <i className="fas fa-comment text-blue-400 mr-2"></i>
                            What are your main ESG reporting challenges?
                        </label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-4 py-3 bg-gray-700/70 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Tell us about your current ESG reporting process and challenges"
                        ></textarea>
                    </div>
                    <div className="flex items-center">
                        <input 
                            type="checkbox" 
                            id="privacy-policy" 
                            required
                            className="w-4 h-4 mr-2 accent-green-500"
                        />
                        <label htmlFor="privacy-policy" className="text-sm text-gray-300">
                            I agree to the <a href="#" className="text-green-400 hover:underline">privacy policy</a> and consent to being contacted about ESG solutions.
                        </label>
                    </div>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full px-8 py-3 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 disabled:from-gray-600 disabled:to-gray-500 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-all"
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-calendar-check mr-2"></i>
                                Schedule My ESG Platform Demo
                            </>
                        )}
                    </button>
                    {submitStatus === 'error' && (
                        <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4 mt-4">
                            <p className="text-red-400 flex items-center">
                                <i className="fas fa-exclamation-circle mr-2"></i>
                                There was an error submitting your inquiry. Please try again.
                            </p>
                        </div>
                    )}
                </form>
            )}
        </div>
    );
}

// Carbon Calculator Component
function CarbonCalculator() {
    const [employees, setEmployees] = React.useState(50);
    const [servers, setServers] = React.useState(5);
    const [desktops, setDesktops] = React.useState(25);
    const [laptops, setLaptops] = React.useState(25);
    const [calculated, setCalculated] = React.useState(false);

    const calculateEmissions = () => {
        // Simple formula for demo purposes
        const serverEmissions = servers * 1.5; // tons per year
        const desktopEmissions = desktops * 0.1; // tons per year
        const laptopEmissions = laptops * 0.05; // tons per year
        
        return serverEmissions + desktopEmissions + laptopEmissions;
    };

    const emissions = calculated ? calculateEmissions() : null;

    return (
        <div>
            <p className="text-gray-300 mb-6">
                Estimate the carbon footprint of your company's computing infrastructure.
            </p>
            <div className="space-y-5 mb-6">
                <div>
                    <label className="block text-sm font-medium mb-2 flex items-center">
                        <i className="fas fa-users text-blue-400 mr-2"></i>
                        Number of Employees
                    </label>
                    <input
                        type="range"
                        min="10"
                        max="1000"
                        step="10"
                        value={employees}
                        onChange={(e) => setEmployees(parseInt(e.target.value))}
                        className="w-full accent-green-500 h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                        <span>10</span>
                        <span className="bg-green-900/40 px-3 py-1 rounded-full text-green-400">{employees}</span>
                        <span>1000</span>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-2 flex items-center">
                        <i className="fas fa-server text-blue-400 mr-2"></i>
                        Number of Servers
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={servers}
                        onChange={(e) => setServers(parseInt(e.target.value))}
                        className="w-full accent-green-500 h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                        <span>0</span>
                        <span className="bg-green-900/40 px-3 py-1 rounded-full text-green-400">{servers}</span>
                        <span>100</span>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-2 flex items-center">
                        <i className="fas fa-desktop text-blue-400 mr-2"></i>
                        Number of Desktops
                    </label>
                    <input
                        type="range"
                        min="0"
                        max={employees}
                        value={desktops}
                        onChange={(e) => setDesktops(parseInt(e.target.value))}
                        className="w-full accent-green-500 h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                        <span>0</span>
                        <span className="bg-green-900/40 px-3 py-1 rounded-full text-green-400">{desktops}</span>
                        <span>{employees}</span>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-2 flex items-center">
                        <i className="fas fa-laptop text-blue-400 mr-2"></i>
                        Number of Laptops
                    </label>
                    <input
                        type="range"
                        min="0"
                        max={employees}
                        value={laptops}
                        onChange={(e) => setLaptops(parseInt(e.target.value))}
                        className="w-full accent-green-500 h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
                    />
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                        <span>0</span>
                        <span className="bg-green-900/40 px-3 py-1 rounded-full text-green-400">{laptops}</span>
                        <span>{employees}</span>
                    </div>
                </div>
            </div>
            <button
                onClick={() => setCalculated(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300 rounded-lg text-white transition-all transform hover:scale-105 flex items-center justify-center mb-6 w-full"
            >
                <i className="fas fa-calculator mr-2"></i>
                Calculate Emissions
            </button>
            {calculated && (
                <div className="bg-gradient-to-br from-gray-700/70 to-gray-800/70 p-6 rounded-lg border border-gray-600">
                    <h3 className="text-lg font-bold mb-2 flex items-center">
                        <i className="fas fa-chart-line text-green-400 mr-2"></i>
                        Estimated Annual CO2 Emissions
                    </h3>
                    <p className="text-4xl font-bold text-green-400 mb-3">{emissions.toFixed(1)} tons</p>
                    <p className="text-sm text-gray-300">
                        Based on industry averages for server, desktop, and laptop energy consumption and carbon intensity.
                    </p>
                    
                    <div className="mt-4 grid grid-cols-3 gap-3">
                        <div className="bg-gray-800/80 p-3 rounded-lg text-center">
                            <p className="text-sm text-gray-400">Servers</p>
                            <p className="text-xl text-green-400 font-bold">{(servers * 1.5).toFixed(1)}</p>
                            <p className="text-xs text-gray-400">tons CO₂</p>
                        </div>
                        <div className="bg-gray-800/80 p-3 rounded-lg text-center">
                            <p className="text-sm text-gray-400">Desktops</p>
                            <p className="text-xl text-green-400 font-bold">{(desktops * 0.1).toFixed(1)}</p>
                            <p className="text-xs text-gray-400">tons CO₂</p>
                        </div>
                        <div className="bg-gray-800/80 p-3 rounded-lg text-center">
                            <p className="text-sm text-gray-400">Laptops</p>
                            <p className="text-xl text-green-400 font-bold">{(laptops * 0.05).toFixed(1)}</p>
                            <p className="text-xs text-gray-400">tons CO₂</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Partner Testimonials Component
function PartnerTestimonials() {
    const testimonials = [
        {
            quote: "The ESG platform transformed our sustainability reporting process. We've cut reporting time by 70% while providing better data to our investors and stakeholders.",
            author: "Sarah Johnson",
            title: "Sustainability Director",
            company: "TechGrowth Solutions"
        },
        {
            quote: "After implementing the ESG analytics dashboard, we identified operational inefficiencies that not only improved our sustainability metrics but saved us £120,000 annually.",
            author: "Michael Chen",
            title: "CEO",
            company: "EcoVentures Inc."
        },
        {
            quote: "Their ESG platform seamlessly integrates with our existing systems and has streamlined our regulatory compliance reporting. The custom metrics feature allowed us to track the KPIs that matter most to our business.",
            author: "Aisha Patel",
            title: "CTO",
            company: "FutureWave Technologies"
        }
    ];

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6 text-center">ESG Platform <span className="text-green-400">Success Stories</span></h2>
            <p className="text-center text-gray-300 mb-8 max-w-3xl mx-auto">
                See how organizations are transforming their sustainability reporting with our enterprise ESG platform.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gradient-to-b from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-8 border border-gray-700 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-green-500/30">
                        <div className="text-green-400 mb-6">
                            <i className="fas fa-quote-left text-3xl opacity-50"></i>
                        </div>
                        <p className="text-gray-300 mb-8 italic text-lg leading-relaxed">"{testimonial.quote}"</p>
                        <div className="flex items-center">
                            <div className="bg-gradient-to-br from-green-500/30 to-green-400/10 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                                <i className="fas fa-user text-green-400"></i>
                            </div>
                            <div>
                                <p className="font-bold text-white">{testimonial.author}</p>
                                <p className="text-gray-400 text-sm">{testimonial.title}</p>
                                <p className="text-green-400 text-sm">{testimonial.company}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PartnerPage({ onBackClick }) {
    try {
        const [selectedOffering, setSelectedOffering] = React.useState(null);

        const handleOfferingSelect = (offeringId) => {
            setSelectedOffering(offeringId);
            // Scroll to the partnership form
            const element = document.getElementById('partnership-form');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        };

        return (
            <section className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white relative overflow-hidden">
                {/* Include SEO component */}
                <PartnerPageSEO />
                
                {/* Background decorative elements */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-green-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-40 left-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
                </div>
                
                <div className="container mx-auto px-4 py-16 relative z-10">
                    <button 
                        onClick={onBackClick}
                        className="mb-8 flex items-center text-green-400 hover:text-green-300 transition-colors back-button group"
                    >
                        <i className="fas fa-arrow-left mr-2 group-hover:transform group-hover:-translate-x-1 transition-transform"></i> Back to Home
                    </button>

                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <div className="inline-block relative">
                                <div className="absolute -inset-1 rounded-full blur-md bg-gradient-to-r from-green-500/30 to-green-300/30"></div>
                                <h1 className="relative text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 text-transparent bg-clip-text">
                                    Enterprise ESG <span className="text-green-400">Platform</span>
                                </h1>
                            </div>
                            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                                Transform your sustainability reporting with our advanced ESG platform. Streamline data collection, gain actionable insights, and meet your regulatory requirements.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4 mt-8">
                                <a href="#esg-platform-section" className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white font-bold transition-all transform hover:scale-105 flex items-center">
                                    <i className="fas fa-chart-line mr-2"></i>
                                    Explore ESG Platform
                                </a>
                                <a href="#partnership-form" className="px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-all transform hover:scale-105 flex items-center">
                                    <i className="fas fa-calendar-check mr-2"></i>
                                    Schedule a Demo
                                </a>
                            </div>
                        </div>

                        <PartnerBenefits />
                        
                        {/* Navigation Menu for Three Sections */}
                        <div className="sticky top-4 z-10 bg-gray-900/90 backdrop-blur-md rounded-xl p-4 mb-16 border border-gray-700 shadow-xl">
                            <div className="flex flex-wrap justify-center gap-4">
                                <button 
                                    onClick={() => document.getElementById('esg-platform-section').scrollIntoView({ behavior: 'smooth' })}
                                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white transition-all transform hover:scale-105 flex items-center"
                                >
                                    <i className="fas fa-chart-line mr-2"></i>
                                    ESG Platform
                                </button>
                                <button 
                                    onClick={() => document.getElementById('carbon-credits-section').scrollIntoView({ behavior: 'smooth' })}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-all transform hover:scale-105 flex items-center"
                                >
                                    <i className="fas fa-leaf mr-2"></i>
                                    Carbon Credits
                                </button>
                                <button 
                                    onClick={() => document.getElementById('sequoia-tons-section').scrollIntoView({ behavior: 'smooth' })}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-all transform hover:scale-105 flex items-center"
                                >
                                    <i className="fas fa-tree mr-2"></i>
                                    Sequoia Tons
                                </button>
                            </div>
                        </div>
                        
                        {/* Three Separate Sections */}
                        
                        {/* ESG Platform Section */}
                        <div id="esg-platform-section" className="mb-24 py-8 bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 border-t-4 border-green-500 shadow-lg">
                            <div className="flex items-center justify-center mb-8">
                                <div className="w-20 h-20 bg-green-500/30 rounded-full flex items-center justify-center mr-4">
                                    <i className="fas fa-chart-line text-green-400 text-3xl"></i>
                                </div>
                                <h2 className="text-4xl font-bold">Enterprise ESG <span className="text-green-400">Platform</span></h2>
                            </div>
                            <p className="text-center text-gray-300 mb-8 max-w-3xl mx-auto text-lg">
                                Our comprehensive ESG data management platform helps you collect, analyze, and report your sustainability metrics with powerful analytics and customizable dashboards.
                            </p>
                            
                            {/* SEO-optimized features list */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                                <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all">
                                    <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                                        <i className="fas fa-database text-green-400 text-2xl"></i>
                                    </div>
                                    <h3 className="font-bold text-xl mb-3 text-center">Automated Data Collection</h3>
                                    <p className="text-sm text-gray-300">Streamline ESG metrics tracking with automated data collection from multiple sources including utilities, facilities, and suppliers.</p>
                                </div>
                                <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all">
                                    <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                                        <i className="fas fa-chart-pie text-green-400 text-2xl"></i>
                                    </div>
                                    <h3 className="font-bold text-xl mb-3 text-center">Real-time Analytics</h3>
                                    <p className="text-sm text-gray-300">Make data-driven sustainability decisions with real-time ESG analytics, custom KPIs, and benchmarking against industry standards.</p>
                                </div>
                                <div className="bg-gradient-to-b from-gray-700/70 to-gray-800/70 p-6 rounded-xl border border-gray-600/50 hover:border-green-500/30 transition-all">
                                    <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                                        <i className="fas fa-file-alt text-green-400 text-2xl"></i>
                                    </div>
                                    <h3 className="font-bold text-xl mb-3 text-center">Regulatory Reporting</h3>
                                    <p className="text-sm text-gray-300">Generate TCFD, SASB, GRI and other compliant ESG reports with one click. Meet global disclosure requirements effortlessly.</p>
                                </div>
                            </div>
                            
                            <ESGPlatformSubscription onRequestInfo={handleOfferingSelect} />
                        </div>
                        
                        {/* Carbon Credits Section */}
                        <div id="carbon-credits-section" className="mb-24 py-8 bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 border-t-4 border-green-500 shadow-lg">
                            <div className="flex items-center justify-center mb-8">
                                <div className="w-20 h-20 bg-green-500/30 rounded-full flex items-center justify-center mr-4">
                                    <i className="fas fa-leaf text-green-400 text-3xl"></i>
                                </div>
                                <h2 className="text-4xl font-bold">Carbon <span className="text-green-400">Credits</span></h2>
                            </div>
                            <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto text-lg">
                                Offset your carbon footprint with our high-quality carbon credits from verified sustainable technology projects.
                            </p>
                            <CarbonCreditsOffering onRequestInfo={handleOfferingSelect} />
                        </div>
                        
                        {/* Sequoia Tons Section */}
                        <div id="sequoia-tons-section" className="mb-24 py-8 bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 border-t-4 border-green-500 shadow-lg">
                            <div className="flex items-center justify-center mb-8">
                                <div className="w-20 h-20 bg-green-500/30 rounded-full flex items-center justify-center mr-4">
                                    <i className="fas fa-tree text-green-400 text-3xl"></i>
                                </div>
                                <h2 className="text-4xl font-bold">Sequoia <span className="text-green-400">Tons</span></h2>
                            </div>
                            <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto text-lg">
                                Make a premium impact by supporting ancient sequoia forests with our Sequoia Tons program, offering guaranteed carbon sequestration across different time horizons.
                            </p>
                            <SequoiaTonsOffering onRequestInfo={handleOfferingSelect} />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
                            <div id="partnership-form" className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                                <div className="bg-green-600/20 border-b border-green-600/30 p-4 text-center">
                                    <h2 className="text-2xl font-bold text-white">Schedule Your ESG Platform Demo</h2>
                                </div>
                                <div className="p-8">
                                    <div className="mb-8">
                                        <h3 className="font-bold text-lg mb-2">Transform Your ESG Reporting</h3>
                                        <p className="text-gray-300">Get a personalized walkthrough of our ESG platform and discover how it can help you streamline sustainability reporting, track metrics, and meet regulatory requirements.</p>
                                    </div>
                                    <PartnershipForm selectedTier={selectedOffering} />
                                </div>
                            </div>
                            <div className="space-y-8">
                                <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                                    <div className="bg-blue-600/20 border-b border-blue-600/30 p-4 text-center">
                                        <h2 className="text-2xl font-bold text-white">Carbon Calculator</h2>
                                    </div>
                                    <div className="p-8">
                                        <CarbonCalculator />
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                                    <div className="bg-purple-600/20 border-b border-purple-600/30 p-4 text-center">
                                        <h2 className="text-2xl font-bold text-white">ESG Platform Benefits</h2>
                                    </div>
                                    <div className="p-8">
                                        <div className="space-y-6">
                                            <div className="flex items-start">
                                                <div className="bg-green-500/20 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                                    <i className="fas fa-certificate text-green-400 text-xl"></i>
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-lg mb-2">Sustainability Intelligence</h3>
                                                    <p className="text-gray-300">Turn your environmental data into actionable insights with our advanced ESG analytics platform.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="bg-green-500/20 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                                    <i className="fas fa-chart-bar text-green-400 text-xl"></i>
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-lg mb-2">TCFD & SASB Compliance</h3>
                                                    <p className="text-gray-300">Meet global ESG reporting standards with built-in frameworks and compliance tools.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="bg-green-500/20 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                                    <i className="fas fa-handshake text-green-400 text-xl"></i>
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-lg mb-2">Investor-Grade Reporting</h3>
                                                    <p className="text-gray-300">Generate professional ESG reports that satisfy investor demands for transparent sustainability data.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl p-10 mb-16">
                            <PartnerTestimonials />
                        </div>
                        
                        {/* ESG Platform FAQ Section - Added for SEO */}
                        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-lg rounded-2xl border border-gray-700 shadow-xl p-10 mb-16">
                            <h2 className="text-3xl font-bold mb-8 text-center">ESG Platform <span className="text-green-400">FAQ</span></h2>
                            <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto">
                                Common questions about our enterprise ESG data management and reporting solution
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                    <h3 className="text-xl font-bold mb-4 flex items-center">
                                        <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                        What ESG reporting frameworks does your platform support?
                                    </h3>
                                    <p className="text-gray-300">
                                        Our ESG platform supports all major reporting frameworks including TCFD, SASB, GRI, CDP, and EU SFDR. The system automatically maps your data to the appropriate disclosure requirements for each framework, eliminating duplicate data entry.
                                    </p>
                                </div>
                                
                                <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                    <h3 className="text-xl font-bold mb-4 flex items-center">
                                        <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                        How does the data collection process work?
                                    </h3>
                                    <p className="text-gray-300">
                                        Our platform offers multiple data collection methods: automated API integrations with existing systems, direct data uploads via CSV/Excel, manual data entry through customizable forms, and a mobile app for field data collection. We can connect to utility providers, ERP systems, and IoT devices.
                                    </p>
                                </div>
                                
                                <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                    <h3 className="text-xl font-bold mb-4 flex items-center">
                                        <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                        Can the ESG platform handle Scope 3 emissions tracking?
                                    </h3>
                                    <p className="text-gray-300">
                                        Yes, our platform has comprehensive capabilities for Scope 3 emissions tracking. We provide supplier assessment tools, spend-based calculation methods, and supply chain mapping features to help you collect and analyze your value chain emissions data in compliance with GHG Protocol standards.
                                    </p>
                                </div>
                                
                                <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                    <h3 className="text-xl font-bold mb-4 flex items-center">
                                        <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                        How customizable are the ESG dashboards and reports?
                                    </h3>
                                    <p className="text-gray-300">
                                        Our dashboards are highly customizable. You can create role-specific views (executive, sustainability team, operations), custom metrics and KPIs, personalized report templates, and branded outputs. The Enterprise tier includes white-labeling options for investor-facing reports.
                                    </p>
                                </div>
                                
                                <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                    <h3 className="text-xl font-bold mb-4 flex items-center">
                                        <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                        What security measures protect our ESG data?
                                    </h3>
                                    <p className="text-gray-300">
                                        Our platform employs enterprise-grade security including SOC 2 compliance, end-to-end encryption, role-based access controls, multi-factor authentication, regular security audits, and GDPR compliance. All data is backed up in multiple secure locations.
                                    </p>
                                </div>
                                
                                <div className="bg-gray-800/70 rounded-xl p-6 border border-gray-700 hover:border-green-500/30 transition-all">
                                    <h3 className="text-xl font-bold mb-4 flex items-center">
                                        <i className="fas fa-question-circle text-green-400 mr-3"></i>
                                        How long does implementation typically take?
                                    </h3>
                                    <p className="text-gray-300">
                                        Basic implementation can be completed in as little as 2-4 weeks. More complex enterprise integrations typically take 6-8 weeks. Our implementation team will create a custom onboarding plan based on your specific requirements and existing systems.
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Final CTA Section focused on ESG Platform */}
                        <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl border border-green-500/30 shadow-xl p-10 mb-8">
                            <div className="text-center max-w-3xl mx-auto">
                                <h2 className="text-3xl font-bold mb-6">Transform Your ESG Reporting Today</h2>
                                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                    Join hundreds of organizations that have revolutionized their sustainability reporting with our enterprise ESG platform. Schedule a demo to see how our solution can help you meet regulatory requirements, impress investors, and drive your sustainability initiatives forward.
                                </p>
                                <div className="flex flex-wrap justify-center gap-6">
                                    <a 
                                        href="#esg-platform-section" 
                                        className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 rounded-lg text-white font-bold text-lg transition-all transform hover:scale-105 flex items-center"
                                    >
                                        <i className="fas fa-chart-line mr-3"></i>
                                        Explore ESG Platform
                                    </a>
                                    <a 
                                        href="#partnership-form" 
                                        className="px-8 py-4 bg-white text-gray-900 hover:bg-gray-100 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center"
                                    >
                                        <i className="fas fa-calendar-check mr-3"></i>
                                        Schedule Your Demo
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('PartnerPage error:', error);
        if (typeof reportError === 'function') {
            reportError(error);
        }
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="text-center p-8 bg-red-900/20 rounded-xl border border-red-700/30 max-w-md">
                    <i className="fas fa-exclamation-triangle text-red-400 text-4xl mb-4"></i>
                    <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
                    <p className="text-gray-300 mb-6">We encountered an error loading the Partner page. Please try again later.</p>
                    <button 
                        onClick={onBackClick}
                        className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }
}

window.PartnerPage = PartnerPage;
export default PartnerPage;
